<template>
  <v-input
    :rules="$attrs.rules"
    :value="value"
    :hide-details="attrs.hideDetails"
    :prepend-icon="$attrs['prepend-icon']"
    :class="{
      'p-reversed': reversed,
      'p-dense': attrs.dense,
      'p-solo': attrs.solo,
      'p-outlined': attrs.outlined,
      'p-implicit': attrs.implicit,
      'p-center': attrs.center
    }"
    validate-on-blur
    class="p-date-time-picker"
  >
    <template v-slot:append>
      <v-icon
        v-if="attrs.clearable"
        :class="{ 'p-visibility-hidden': !value }"
        @click="onClear"
      >
        mdi-close
      </v-icon>
    </template>

    <div
      :class="{ 'ml-2': reversed && !dateOnly }"
      class="p-date-time-picker__date"
    >
      <v-menu
        v-model="menu"
        :close-on-content-click="false"
        min-width="auto"
        transition="scale-transition"
        offset-y
      >
        <template v-slot:activator="{ on }">
          <v-text-field
            :value="displayDate"
            v-on="on"
            :placeholder="datePlaceholder"
            :label="dateOnly || reversed ? '' : $attrs.label"
            :class="dateClass"
            :disabled="$attrs.disabled"
            :dense="$attrs.dense"
            :solo="$attrs.solo"
            :outlined="$attrs.outlined"
            readonly
            hide-details
            prepend-icon=""
            @click="$emit('click')"
          ></v-text-field>
        </template>
        <v-card outlined>
          <v-date-picker
            v-model="date"
            :min="minDate"
            :max="maxDate"
            color="primary"
            no-title
            @input="menu = false"
            @change="$emit('change')"
          ></v-date-picker>
        </v-card>
      </v-menu>
    </div>
    <v-combobox
      ref="timepicker"
      v-model="time"
      v-if="!dateOnly"
      :placeholder="timePlaceholder"
      :label="reversed ? $attrs.label : ''"
      :items="timeItems"
      :disabled="$attrs.disabled"
      :dense="$attrs.dense"
      :solo="$attrs.solo"
      :outlined="$attrs.outlined"
      :class="[timeClass, {'ml-2': !reversed }]"
      class="p-date-time-picker__time"
      append-icon=""
      prepend-icon=""
      hide-details
      @change="onTimeChange"
    >
      <template v-slot:item="{ item, on, attrs }">
        <v-list-item v-on="on" v-bind="attrs" dense>
          <v-list-item-content>
            <v-list-item-title>{{ item }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-combobox>
  </v-input>
</template>


<script>
// IMPORTANT NOTE: this component works only with UTC dates - accepts UTC, returns UTC, but displays local
import moment from 'moment'
import { dateTimeFormats } from '@/constants'


export default {
  inheritAttrs: false,


  props: {
    value: {
      type: String
    },
    min: {
      type: String
    },
    max: {
      type: String
    },
    dateOnly: {
      type: Boolean,
      default: false
    },
    reversed: {
      type: Boolean,
      default: false
    },
    dateClass: {
      type: undefined
    },
    timeClass: {
      type: undefined
    },
    datePlaceholder: {
      type: String,
      default: ''
    },
    timePlaceholder: {
      type: String,
      default: ''
    },
    allDayClicked: {
      type: Boolean,
      default: false
    }
  },


  data () {
    return {
      menu: false,
      dateFormat: 'YYYY-MM-DD',
      timeFormat: 'HH:mm',
      defaultTimeString: '00:00'
    }
  },


  computed: {
    valueLocal: {
      get () {
        if (!this.value) {
          return null
        }

        if (this.dateOnly) {
          return moment(this.value, this.dateFormat)
        }

        return moment.utc(this.value).local()
      }
    },

    date: {
      get () {
        return this.valueLocal?.format(this.dateFormat)
      },
      set (val) {
        if (!this.time) {
          this.$refs.timepicker.$refs.input.click()
        }

        this.emitValue(val, this.time)
      }
    },

    time: {
      get () {
        return this.valueLocal?.format(this.timeFormat)
      },
      set (val) {
        this.emitValue(this.date, val)
      }
    },

    minDate () {
      return this.min ? moment.utc(this.min).local().format(this.dateFormat) : null
    },

    maxDate () {
      return this.max ? moment.utc(this.max).local().format(this.dateFormat) : null
    },

    displayDate () {
      return this.valueLocal?.format('ll') ?? ''
    },

    timeItems () {
      const array = []
      for (let hour = 0; hour < 24; hour++) {
        for (let min = 0; min < 60; min += 15) {
          array.push(moment({ hour: hour, minute: min }).format(this.timeFormat))
        }
      }
      return array
    },

    attrs () {
      return {
        hideDetails: this.$attrs.hasOwnProperty('hide-details') ? (this.$attrs['hide-details'] ?? false) : false,
        clearable: this.$attrs.hasOwnProperty('clearable') && this.$attrs.clearable !== false,
        dense: this.$attrs.hasOwnProperty('dense') && this.$attrs.dense !== false,
        solo: this.$attrs.hasOwnProperty('solo') && this.$attrs.solo !== false,
        outlined: this.$attrs.hasOwnProperty('outlined') && this.$attrs.outlined !== false,
        implicit: this.$attrs.hasOwnProperty('implicit') && this.$attrs.implicit !== false,
        center: this.$attrs.hasOwnProperty('center') && this.$attrs.center !== false
      }
    }
  },


  watch: {
    min: {
      handler (min) {
        if (min) {
          if (moment(this.date).isBefore(this.minDate, 'day')) {
            this.date = this.minDate
          }
        }
      },
      immediate: true
    },

    max: {
      handler (max) {
        if (max) {
          if (moment(this.date).isAfter(this.maxDate, 'day')) {
            this.date = this.maxDate
          }
        }
      },
      immediate: true
    },

    dateOnly: {
      handler () {
        if (this.date) {
          if (this.dateOnly) {
            const date = moment.utc(this.value).local().format(this.dateFormat)
            this.emitValue(date, this.defaultTimeString)
          } else {
            let dateTime
            if (this.allDayClicked) {
              dateTime = moment(this.date)
            } else {
              dateTime = moment.utc(this.value).local()
            }
            this.emitValue(dateTime.format(this.dateFormat), dateTime.format(this.timeFormat))
          }
        }
      },
      immediate: false
    }
  },


  methods: {
    onTimeChange (val) {
      if (val === undefined) return
      const momentTime = moment(val, this.timeFormat)
      const validTime = momentTime.isValid() ? momentTime.format(this.timeFormat) : this.defaultTimeString

      this.$nextTick(function () {
        this.time = validTime
      })
    },

    onClear () {
      this.date = null
      this.$nextTick(() => {
        this.emitValue(this.date, this.time)
      })
    },

    emitValue (date, time) {
      let dateTime = null

      if (date) {
        if (this.dateOnly) {
          dateTime = moment.utc(date, this.dateFormat).format(dateTimeFormats.dateTimeUtc)
        } else {
          dateTime = moment(date + time, this.dateFormat + this.timeFormat).utc().format(dateTimeFormats.dateTimeUtc)
        }
      }

      this.$emit('input', dateTime)
    }
  }
}
</script>
